import { FileText, User, Globe, UserCheck, MessageSquare, Image, CreditCard, ShieldCheck, Building, Lock, XCircle, AlertTriangle, Scale, RefreshCw, Mail, Heart } from 'lucide-react';
import LegalLayout, { LegalSection, BulletList } from '../../components/LegalLayout';

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      badge="Legal Agreement"
      badgeIcon={FileText}
      badgeColor="#C8553D"
      lastUpdated="April 5, 2026"
      version="1.0"
      introBorderColor="#C8553D"
      intro="Welcome to AfroConnect. By accessing or using our mobile application, website, and services, you agree to be bound by these Terms of Service."
    >
      <LegalSection number="1" title="Acceptance of Terms" icon={FileText}>
        <p>By creating an account or using AfroConnect, you confirm that you are at least 18 years old and agree to comply with these Terms. If you do not agree, please do not use our services.</p>
      </LegalSection>

      <LegalSection number="2" title="Eligibility" icon={Globe}>
        <p>AfroConnect is available exclusively to users located in Canada. You must:</p>
        <BulletList items={[
          'Be at least 18 years of age',
          'Be legally able to enter into binding contracts',
          'Not be prohibited from using our services under applicable law',
          'Provide accurate and truthful information',
          'Not have been previously banned from AfroConnect',
        ]} />
      </LegalSection>

      <LegalSection number="3" title="Account Registration" icon={User}>
        <p>To use AfroConnect, you must create an account by providing:</p>
        <BulletList items={[
          'A valid email address',
          'A secure password',
          'Accurate profile information',
          'At least 2 authentic photos of yourself',
        ]} />
        <p>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</p>
      </LegalSection>

      <LegalSection number="4" title="User Conduct" icon={XCircle} iconColor="#E76F51">
        <p>You agree <strong>NOT</strong> to:</p>
        <BulletList items={[
          'Harass, abuse, or threaten other users',
          'Post false, misleading, or deceptive content',
          'Impersonate another person',
          'Use the service for commercial solicitation',
          'Share contact information for fraudulent purposes',
          'Post inappropriate, explicit, or offensive content',
          'Use automated systems to access the service',
          'Attempt to circumvent security measures',
        ]} />
      </LegalSection>

      <LegalSection number="5" title="Content Guidelines" icon={Image}>
        <p>All content you submit must:</p>
        <BulletList items={[
          'Be your own original content or content you have rights to share',
          'Not infringe on intellectual property rights',
          'Not contain illegal content',
          'Not promote violence, discrimination, or illegal activities',
        ]} />
        <p>We reserve the right to remove any content that violates these guidelines.</p>
      </LegalSection>

      <LegalSection number="6" title="Premium Subscriptions" icon={CreditCard}>
        <p>AfroConnect offers premium subscription plans:</p>
        <BulletList items={[
          'Monthly Plan: $9.99 CAD/month',
          'Yearly Plan: $79.99 CAD/year',
        ]} />
        <p>Subscriptions auto-renew unless cancelled 24 hours before the renewal date. Payments are processed securely through Stripe. Refunds are handled according to our refund policy.</p>
      </LegalSection>

      <LegalSection number="7" title="User Verification" icon={UserCheck} iconColor="#2A9D8F">
        <p>We offer optional selfie verification to enhance trust and safety. By participating:</p>
        <BulletList items={[
          'You consent to our use of facial recognition technology (Azure Face API)',
          'Your selfie will be compared to your profile photos',
          'Verified users receive a verified badge',
          'Verification data is handled per our Privacy Policy',
        ]} />
      </LegalSection>

      <LegalSection number="8" title="Business, Event & Community Listings" icon={Building}>
        <p>Users may submit businesses, events, and communities for listing. All submissions:</p>
        <BulletList items={[
          'Require admin approval before publishing',
          'Must contain accurate information',
          'Must comply with all applicable laws',
          'May be removed at our discretion',
        ]} />
      </LegalSection>

      <LegalSection number="9" title="Privacy" icon={Lock} iconColor="#2A9D8F">
        <p>Your privacy is important to us. Please review our <a href="/privacy">Privacy Policy</a> to understand how we collect, use, and protect your personal information.</p>
      </LegalSection>

      <LegalSection number="10" title="Termination" icon={AlertTriangle} iconColor="#E76F51">
        <p>We may suspend or terminate your account if you:</p>
        <BulletList items={[
          'Violate these Terms',
          'Engage in fraudulent activity',
          'Receive multiple user reports',
          'Pose a safety risk to other users',
        ]} />
        <p>You may delete your account at any time through the Settings menu.</p>
      </LegalSection>

      <LegalSection number="11" title="Disclaimers" icon={ShieldCheck}>
        <p>AfroConnect is provided <strong>"as is"</strong> without warranties of any kind. We do not guarantee:</p>
        <BulletList items={[
          'Compatibility with any particular user',
          'Accuracy of user-provided information',
          'Uninterrupted or error-free service',
          'The conduct of other users',
        ]} />
      </LegalSection>

      <LegalSection number="12" title="Limitation of Liability" icon={Scale}>
        <p>To the maximum extent permitted by law, AfroConnect shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service.</p>
      </LegalSection>

      <LegalSection number="13" title="Changes to Terms" icon={RefreshCw}>
        <p>We may update these Terms from time to time. Continued use of AfroConnect after changes constitutes acceptance of the new Terms. We will notify users of significant changes via email or in-app notification.</p>
      </LegalSection>

      <LegalSection number="14" title="Contact Us" icon={Mail}>
        <p>If you have questions about these Terms, please contact us at:</p>
        <BulletList items={[
          <><strong>Email:</strong> <a href="mailto:legal@afroconnect.ca">legal@afroconnect.ca</a></>,
          <><strong>Address:</strong> Toronto, Ontario, Canada</>,
        ]} />
      </LegalSection>

      <div className="text-center pt-4 flex items-center justify-center gap-2 text-[#6D6875] text-sm">
        <Heart className="w-4 h-4 text-[#C8553D]" />
        <span>Thank you for being part of AfroConnect</span>
      </div>
    </LegalLayout>
  );
}
